/**
 * Time Distribution Chart Utilities
 * Handles time bucket calculations and data processing for dual bar charts
 */

export interface TimeBucket {
  min: number;
  max: number;
  label: string;
  color: string;
}

export interface TimeBucketData {
  count: number;
  percentage: number;
  avgTime?: number;
}

export interface TimeDistributionData {
  [bucketKey: string]: TimeBucketData;
}

// First Response Time Buckets (in hours) - Green gradient from light to dark
export const RESPONSE_TIME_BUCKETS: Record<string, TimeBucket> = {
  '0-1h': { min: 0, max: 1, label: '0-1 hour', color: '#A7F3D0' }, // Light green (fastest)
  '1-3h': { min: 1, max: 3, label: '1-3 hours', color: '#6EE7B7' }, //
  '3-6h': { min: 3, max: 6, label: '3-6 hours', color: '#34D399' }, //
  '6-12h': { min: 6, max: 12, label: '6-12 hours', color: '#10B981' }, //
  '12-24h': { min: 12, max: 24, label: '12-24 hours', color: '#059669' }, //
  '24h+': { min: 24, max: Infinity, label: '24+ hours', color: '#047857' }, // Dark green (slowest)
};

// Resolution Time Buckets (in hours) - Blue gradient from light to dark
export const RESOLUTION_TIME_BUCKETS: Record<string, TimeBucket> = {
  '0-6h': { min: 0, max: 6, label: '0-6 hours', color: '#BFDBFE' }, // Light blue (fastest)
  '6-12h': { min: 6, max: 12, label: '6-12 hours', color: '#93C5FD' }, //
  '12-24h': { min: 12, max: 24, label: '12-24 hours', color: '#60A5FA' }, //
  '1-3d': { min: 24, max: 72, label: '1-3 days', color: '#3B82F6' }, //
  '3-7d': { min: 72, max: 168, label: '3-7 days', color: '#2563EB' }, //
  '7d+': { min: 168, max: Infinity, label: '7+ days', color: '#1D4ED8' }, // Dark blue (slowest)
};

/**
 * Generate color based on case count intensity
 * Higher counts get darker/more intense colors
 */
export function getCountBasedColor(
  count: number,
  maxCount: number,
  baseColor: 'green' | 'blue'
): string {
  if (maxCount === 0) return baseColor === 'green' ? '#A7F3D0' : '#BFDBFE';

  // Calculate intensity (0 to 1)
  const intensity = count / maxCount;

  if (baseColor === 'green') {
    // Green gradient from light to dark based on count
    if (intensity >= 0.8) return '#047857'; // Darkest green (highest counts)
    if (intensity >= 0.6) return '#059669';
    if (intensity >= 0.4) return '#10B981';
    if (intensity >= 0.2) return '#34D399';
    if (intensity >= 0.1) return '#6EE7B7';

    return '#A7F3D0'; // Lightest green (lowest counts)
  } else {
    // Blue gradient from light to dark based on count
    if (intensity >= 0.8) return '#1D4ED8'; // Darkest blue (highest counts)
    if (intensity >= 0.6) return '#2563EB';
    if (intensity >= 0.4) return '#3B82F6';
    if (intensity >= 0.2) return '#60A5FA';
    if (intensity >= 0.1) return '#93C5FD';

    return '#BFDBFE'; // Lightest blue (lowest counts)
  }
}

/**
 * Update bucket colors based on case counts
 */
function updateBucketColors(
  buckets: Record<string, TimeBucket>,
  data: TimeDistributionData,
  baseColor: 'green' | 'blue'
): Record<string, TimeBucket> {
  // Find the maximum count across all buckets
  const maxCount = Math.max(...Object.values(data).map(bucket => bucket.count));

  // Create new buckets with updated colors
  const updatedBuckets: Record<string, TimeBucket> = {};

  Object.entries(buckets).forEach(([key, bucket]) => {
    const count = data[key]?.count || 0;
    updatedBuckets[key] = {
      ...bucket,
      color: getCountBasedColor(count, maxCount, baseColor),
    };
  });

  return updatedBuckets;
}

/**
 * Find which time bucket a given time value belongs to
 */
function findTimeBucket(timeInHours: number, buckets: Record<string, TimeBucket>): string {
  for (const [key, bucket] of Object.entries(buckets)) {
    if (timeInHours >= bucket.min && timeInHours < bucket.max) {
      return key;
    }
  }

  // Default to last bucket if no match found
  const bucketKeys = Object.keys(buckets);

  return bucketKeys[bucketKeys.length - 1];
}

/**
 * Process raw Salesforce data into time distribution buckets
 */
function processTimeDistribution(
  rawData: Array<{ Time_to_First_Response__c?: number; Time_to_Resolution__c?: number }>,
  buckets: Record<string, TimeBucket>,
  timeField: 'Time_to_First_Response__c' | 'Time_to_Resolution__c'
): TimeDistributionData {
  const distribution: TimeDistributionData = {};

  // Initialize all buckets with 0
  Object.keys(buckets).forEach(key => {
    distribution[key] = { count: 0, percentage: 0, avgTime: 0 };
  });

  let totalCases = 0;
  let totalTimeSum = 0;
  const bucketTimeSums: Record<string, number> = {};

  // Initialize bucket time sums
  Object.keys(buckets).forEach(key => {
    bucketTimeSums[key] = 0;
  });

  // Process each data point and assign to appropriate bucket
  rawData.forEach(dataPoint => {
    const timeInHours = dataPoint[timeField];

    if (timeInHours !== null && timeInHours !== undefined) {
      const bucketKey = findTimeBucket(timeInHours, buckets);
      distribution[bucketKey].count += 1; // Each record represents one case
      bucketTimeSums[bucketKey] += timeInHours;
      totalCases += 1;
      totalTimeSum += timeInHours;
    }
  });

  // Calculate percentages and average times
  Object.keys(distribution).forEach(key => {
    if (totalCases > 0) {
      distribution[key].percentage = (distribution[key].count / totalCases) * 100;
    }

    if (distribution[key].count > 0) {
      distribution[key].avgTime = bucketTimeSums[key] / distribution[key].count;
    }
  });

  return distribution;
}

/**
 * Format time duration for display (e.g., "4.5h", "2.3d")
 */
export function formatTimeDuration(hours: number): string {
  if (hours < 24) {
    return `${hours.toFixed(1)}h`;
  } else if (hours < 168) {
    const days = hours / 24;

    return `${days.toFixed(1)}d`;
  } else {
    const weeks = hours / 168;

    return `${weeks.toFixed(1)}w`;
  }
}

/**
 * Get the maximum count from distribution data for chart scaling
 */
export function getMaxCount(distribution: TimeDistributionData): number {
  return Math.max(...Object.values(distribution).map(bucket => bucket.count));
}

/**
 * Get sorted bucket keys by their min value for consistent ordering
 */
export function getSortedBucketKeys(buckets: Record<string, TimeBucket>): string[] {
  return Object.keys(buckets).sort((a, b) => buckets[a].min - buckets[b].min);
}
